import React from 'react';
import { Link } from 'react-router-dom';

// Categorized modules with competitive replacements
const moduleCategories = [
  {
    category: '💡 Ideate & Plan',
    description: 'From concept to strategy',
    modules: [
      {
        title: 'AI Business Consultant',
        description: 'An always-on advisor that remembers your conversations, reads live CRM metrics, and executes tasks like LinkedIn posts directly from chat.',
        icon: '🤖',
        to: '/modules/business-consultant',
        gradient: 'from-blue-500 to-cyan-500',
        replaces: 'ChatGPT + Notion AI',
      },
      {
        title: 'AI Business Plan Generator',
        description: 'Create investor-ready plans in minutes with structured sections, market insights, and export-ready formats synced to your workspace.',
        icon: '📝',
        to: '/modules/business-plan',
        gradient: 'from-violet-500 to-purple-500',
        replaces: 'LivePlan + Enloop',
      },
      {
        title: 'Brand Kit Creator',
        description: 'Complete brand identity suite: business name, AI logo, email signature, letterhead, and compliments slips. 6 professional tools in one seamless workflow.',
        icon: '🎨',
        to: '/modules/brand-kit-creator',
        gradient: 'from-amber-500 to-orange-500',
        replaces: 'Looka + Namecheap + 99designs',
      },
      {
        title: 'AI Finance Command Center',
        description: 'Forecast cash, model break-even points, and automate reporting without juggling fragile spreadsheets or consultant retainers.',
        icon: '💰',
        to: '/modules/financial-planning',
        gradient: 'from-green-500 to-emerald-500',
        replaces: 'Excel + Google Sheets',
      },
      {
        title: 'AI Pricing Intelligence Suite',
        description: 'Model prices, track margins, and generate strategy playbooks without spreadsheet errors or agency retainers.',
        icon: '💲',
        to: '/modules/pricing-intelligence',
        gradient: 'from-cyan-500 to-blue-500',
        replaces: 'Price Intelligently + Profitwell',
      },
    ],
  },
  {
    category: '🚀 Market & Sell',
    description: 'Attract and convert customers',
    modules: [
      {
        title: 'AI Cold Email Engine',
        description: 'Launch multi-step cold email campaigns with mailbox warm-up, AI copy, and CRM-ready analytics without Instantly or agency retainers.',
        icon: '📧',
        to: '/modules/cold-email',
        gradient: 'from-red-500 to-pink-500',
        replaces: 'Instantly + Lemlist',
      },
      {
        title: 'AI LinkedIn Enhancer',
        description: 'Unify founder and company LinkedIn messaging, automate updates, and hand off approved copy to outreach, recruiting, and investor workflows.',
        icon: '💼',
        to: '/modules/linkedin-enhancer',
        gradient: 'from-blue-600 to-indigo-600',
        replaces: 'Taplio + Shield',
      },
      {
        title: 'AI Social Media Planner',
        description: 'Plan, schedule, and analyze social campaigns with AI-generated posts, calendar automation, and analytics synced to your CRM.',
        icon: '📱',
        to: '/modules/social-media-planner',
        gradient: 'from-pink-500 to-rose-500',
        replaces: 'Buffer + Hootsuite',
      },
      {
        title: 'AI Press Release Generator',
        description: 'Draft AP-style press releases with quotes, boilerplates, and media contacts in minutes, then push them to your consultant and outreach tools.',
        icon: '📢',
        to: '/modules/press-release-generator',
        gradient: 'from-orange-500 to-red-500',
        replaces: 'PR Newswire + Cision',
      },
      {
        title: 'SEO Content Hub',
        description: 'Complete SEO workflow: Generate topical authority maps, create optimized blog posts with keyword control, and manage your content library all in one place.',
        icon: '📝',
        to: '/modules/seo-content-hub',
        gradient: 'from-green-500 to-teal-500',
        replaces: 'SurferSEO + Jasper',
      },
      {
        title: 'SEO Tools Hub',
        description: 'Analyze your real Google & Bing data for maximum SEO accuracy. Free technical audits, Search Console insights, and keyword research. Premium features coming soon.',
        icon: '🔍',
        to: '/modules/seo-tools-hub',
        gradient: 'from-cyan-500 to-blue-500',
        replaces: 'Ahrefs + SEMrush',
      },
      {
        title: 'AI Affiliate Marketing Engine',
        description: 'Recruit partners, automate tiered payouts, and track attribution across your go-to-market stack without juggling campaigns or agencies.',
        icon: '🤝',
        to: '/modules/affiliate-marketing',
        gradient: 'from-teal-500 to-cyan-500',
        replaces: 'Impact + PartnerStack',
      },
      {
        title: 'AI Link Intelligence Suite',
        description: 'Generate branded QR codes, shorten URLs, and stream analytics to CRM without paying Bitly seat pricing or agency change fees.',
        icon: '🔗',
        to: '/modules/link-intelligence',
        gradient: 'from-slate-500 to-gray-600',
        replaces: 'Bitly + QR Code Monkey',
      },
      {
        title: 'Ecommerce Hub',
        description: 'AI-powered product content generation: Create compelling product descriptions, titles, and SEO-optimized copy that converts visitors into customers.',
        icon: '🛒',
        to: '/modules/ecommerce-hub',
        gradient: 'from-orange-500 to-red-500',
        replaces: 'Shopify Apps + Jasper',
      },
    ],
  },
  {
    category: '🧠 Manage & Scale',
    description: 'Operations, team, and growth',
    modules: [
      {
        title: 'AI Customer CRM',
        description: 'Centralize contacts, deals, tasks, and AI-guided follow-ups while staying connected to WhatsApp, email, and the Business Consultant.',
        icon: '👥',
        to: '/modules/customer-crm',
        gradient: 'from-emerald-500 to-teal-500',
        replaces: 'HubSpot + Salesforce',
      },
      {
        title: 'AI Project Management Hub',
        description: 'Manage projects with AI task generation, custom reports, dependency graphs, and team workload tracking—135% feature parity vs Monday.com Pro at 66% less cost.',
        icon: '📋',
        to: '/modules/project-management',
        gradient: 'from-indigo-500 to-purple-500',
        replaces: 'Monday.com + Asana + Zapier',
      },
      {
        title: 'AI Revenue Operations Hub',
        description: 'Track sales, monitor spend, and surface runway alerts without stitching spreadsheets or waiting on finance consultants.',
        icon: '📊',
        to: '/modules/revenue-operations',
        gradient: 'from-purple-500 to-pink-500',
        replaces: 'Excel + Google Sheets',
      },
      {
        title: 'AI Supplier Operations Hub',
        description: 'Source trade suppliers, manage CRM pipelines, and automate compliance without spreadsheet trackers or procurement retainers.',
        icon: '🏭',
        to: '/modules/supplier-operations',
        gradient: 'from-indigo-500 to-blue-500',
        replaces: 'Procurify + Coupa',
      },
      {
        title: 'AI Risk Management Command Center',
        description: 'Score risks, assign mitigation, and publish executive dashboards without spreadsheet chaos or enterprise GRC retainers.',
        icon: '⚠️',
        to: '/modules/risk-management',
        gradient: 'from-yellow-500 to-orange-500',
        replaces: 'LogicGate + Resolver',
      },
      {
        title: 'AI Investor CRM & Fundraising Hub',
        description: 'Manage investor outreach, track pipeline stages, and automate follow-ups without spreadsheet chaos or investor finder retainers.',
        icon: '💎',
        to: '/modules/investor-crm',
        gradient: 'from-violet-500 to-fuchsia-500',
        replaces: 'Affinity + Carta',
      },
      {
        title: 'AI Legal & HR Document Hub',
        description: 'Generate compliant policies, contracts, and playbooks without copying templates or hiring legal and HR advisors.',
        icon: '⚖️',
        to: '/modules/legal-hr',
        gradient: 'from-gray-600 to-slate-700',
        replaces: 'LegalZoom + Rocket Lawyer',
      },
      {
        title: 'AI Staff Management & HR Hub',
        description: 'Manage staff records, time tracking, performance reviews, leave accruals, org charts, and onboarding—without BambooHR or Gusto per-employee pricing.',
        icon: '👔',
        to: '/modules/staff-management',
        gradient: 'from-emerald-600 to-green-600',
        replaces: 'BambooHR + Gusto',
      },
      {
        title: 'AI Recruitment & ATS Platform',
        description: 'White-label careers portal, AI resume screening, pipeline automation, and interview scheduling—without Greenhouse or Lever per-seat fees.',
        icon: '💼',
        to: '/modules/recruitment',
        gradient: 'from-violet-600 to-purple-600',
        replaces: 'Greenhouse + Lever',
      },
      {
        title: 'AI Support Desk',
        description: 'Enterprise support platform with AI responses, multi-channel inbox, SLA tracking, and unlimited agents—95% feature parity with Zendesk at 70% less cost.',
        icon: '🎧',
        to: '/modules/support-desk',
        gradient: 'from-blue-600 to-cyan-600',
        replaces: 'Zendesk + Intercom',
      },
    ],
  },
];

const ModuleGrid: React.FC = () => {
  return (
    <section className="py-20 relative overflow-hidden bg-gradient-to-b from-white via-slate-50 to-white">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
      </div>

      <div className="container relative z-10 mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 mb-4 text-sm font-semibold text-blue-600 bg-blue-50 rounded-full">
            Complete Business Platform
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
            24 AI-Powered Modules
            <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              One Fixed Monthly Price
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
            Everything you need to launch, manage, and scale your business — built into one interconnected AI platform.
          </p>
          <div className="inline-block px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full">
            <p className="text-white font-bold text-lg">
              💰 Replaces HubSpot + Asana + Zendesk + Jasper + BambooHR + Instantly + 10 more tools
            </p>
          </div>
        </div>

        {/* Categorized Modules */}
        {moduleCategories.map((category, categoryIndex) => (
          <div key={category.category} className="mb-16">
            {/* Category Header */}
            <div className="mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-2">
                {category.category}
              </h3>
              <p className="text-lg text-gray-600">{category.description}</p>
            </div>

            {/* Category Modules Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {category.modules.map((module, index) => (
                <Link
                  key={module.title}
                  to={module.to}
                  className="group relative overflow-hidden rounded-2xl bg-white border border-gray-200 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                  style={{ animationDelay: `${(categoryIndex * 100 + index * 50)}ms` }}
                >
                  {/* Gradient accent on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${module.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                  
                  {/* Icon */}
                  <div className={`inline-flex items-center justify-center w-14 h-14 mb-4 rounded-xl bg-gradient-to-br ${module.gradient} text-white text-2xl shadow-md group-hover:scale-110 transition-transform duration-300`}>
                    {module.icon}
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
                    {module.title}
                  </h3>
                  
                  {/* Replaces badge */}
                  <div className="mb-3">
                    <span className="inline-block px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-50 rounded-full border border-blue-200">
                      Replaces: {module.replaces}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {module.description}
                  </p>

                  {/* Arrow icon */}
                  <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                    <span>Explore module</span>
                    <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}

        {/* CTA Section */}
        <div className="text-center mt-16 p-8 md:p-12 rounded-3xl bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Replace Your Entire Software Stack?
          </h3>
          <p className="text-lg md:text-xl text-white mb-3 max-w-2xl mx-auto font-semibold">
            Join thousands of businesses saving 70% on software costs
          </p>
          <p className="text-base text-blue-50 mb-6 max-w-2xl mx-auto">
            Start your 7-day free trial and get instant access to all 23 modules plus 40+ additional tools. Cancel anytime.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center px-10 py-5 text-lg md:text-xl font-bold text-blue-600 bg-white rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Start Free Trial Now
            <svg className="w-6 h-6 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
          <p className="text-sm text-white mt-4 font-medium">
            ✨ All 24 modules • AI Consultant • 40+ tools • £19/month
          </p>
        </div>
      </div>
    </section>
  );
};

export default ModuleGrid;
